import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import SpecFormModal from '../components/SpecFormModal';
import { deleteProblemSpec, fetchProblemSpecs } from '../services/api';
import { useCan } from '../hooks/useCan';
import './SpecPage.css';

const SEVERITY_LABEL = {
  high: 'สูง',
  medium: 'กลาง',
  low: 'ต่ำ',
};

function limitText(text, max = 120) {
  const t = String(text || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 800px)').matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 800px)');
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return isDesktop;
}

export default function SpecPage() {
  const isDesktop = useIsDesktop();
  const canManage = useCan('masters.manage');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      setRows((await fetchProblemSpecs()) || []);
    } catch (err) {
      setError(err.message || 'โหลดสเปคไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (severityFilter !== 'all' && row.severity !== severityFilter) return false;
      if (!q) return true;
      const hay = `${row.nameTh || ''} ${row.nameEn || ''} ${row.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, severityFilter]);

  async function handleDelete(row) {
    if (!window.confirm(`ลบปัญหา "${row.nameTh}" ?`)) return;
    try {
      await deleteProblemSpec(row.id);
      await load();
    } catch (err) {
      setError(err.message || 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div className="spec-page stack">
      <header className="data-hero spec-hero">
        <div>
          <p className="data-kicker">กฎการตรวจ</p>
          <h1>สเปคปัญหา</h1>
          <p className="muted data-sub">{rows.length} รายการในระบบ</p>
        </div>
        {canManage ? (
          isDesktop ? (
            <button
              type="button"
              className="btn btn-primary btn-pill spec-add-btn"
              onClick={() => setModalOpen(true)}
            >
              <span className="spec-add-plus" aria-hidden="true">+</span>
              เพิ่มปัญหา
            </button>
          ) : (
            <Link className="btn btn-primary btn-pill spec-add-btn" to="/spec/new">
              <span className="spec-add-plus" aria-hidden="true">+</span>
              เพิ่ม
            </Link>
          )
        ) : null}
      </header>

      <section className="card spec-toolbar-card">
        <div className="spec-toolbar">
          <input
            className="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ค้นหาชื่อปัญหา..."
            enterKeyHint="search"
          />
          <select
            className="spec-filter"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            aria-label="กรองระดับความรุนแรง"
          >
            <option value="all">ทุกระดับ</option>
            <option value="high">สูง</option>
            <option value="medium">กลาง</option>
            <option value="low">ต่ำ</option>
          </select>
        </div>
      </section>

      {loading ? <p className="muted">กำลังโหลด...</p> : null}
      {error ? <p style={{ color: 'var(--ng)', margin: 0 }}>{error}</p> : null}

      {!loading && filtered.length === 0 ? (
        <section className="card spec-empty">
          <p className="muted" style={{ margin: 0 }}>
            {query.trim() || severityFilter !== 'all'
              ? 'ไม่พบสเปคที่ค้นหา'
              : 'ยังไม่มีสเปคปัญหา'}
          </p>
          {!query.trim() && severityFilter === 'all' && canManage ? (
            <Link className="btn btn-primary" to="/spec/new">
              เพิ่มปัญหาแรก
            </Link>
          ) : null}
        </section>
      ) : null}

      {filtered.length > 0 ? (
        <section className="card spec-list-card">
          <div className="spec-table-head" aria-hidden="true">
            <span>ปัญหา</span>
            <span>เกณฑ์</span>
            <span>ระดับ</span>
            <span>จัดการ</span>
          </div>
          <div className="spec-list">
            {filtered.map((row) => {
              const unit = row.unit || 'mm';
              const severity = row.severity || 'medium';
              return (
                <article key={row.id} className={`spec-row severity-${severity}`}>
                  <div className="spec-row-body">
                    <div className="spec-row-title">
                      <strong>{row.nameTh}</strong>
                      <span className={`badge severity-${severity} spec-badge-mobile`}>
                        {SEVERITY_LABEL[severity] || severity}
                      </span>
                    </div>
                    {row.nameEn ? (
                      <p className="muted spec-row-en">{row.nameEn}</p>
                    ) : null}
                    {row.description ? (
                      <p className="muted spec-row-desc">{limitText(row.description)}</p>
                    ) : null}
                  </div>

                  <div className="spec-row-metrics">
                    <span className="spec-metric">≤ {row.maxSize} {unit}</span>
                    <span className="spec-metric">สูงสุด {row.maxCount} /แผ่น</span>
                  </div>

                  <div className="spec-row-severity">
                    <span className={`badge severity-${severity}`}>
                      {SEVERITY_LABEL[severity] || severity}
                    </span>
                  </div>

                  {canManage ? (
                    <div className="spec-row-actions">
                      <Link className="btn" to={`/spec/${row.id}/edit`}>
                        แก้ไข
                      </Link>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleDelete(row)}
                        aria-label={`ลบ ${row.nameTh}`}
                      >
                        ลบ
                      </button>
                    </div>
                  ) : (
                    <div className="spec-row-actions" />
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      <SpecFormModal
        open={modalOpen}
        mode="create"
        onClose={() => setModalOpen(false)}
        onSaved={async () => {
          try {
            setRows((await fetchProblemSpecs()) || []);
          } catch (err) {
            setError(err.message || 'โหลดสเปคไม่สำเร็จ');
          }
        }}
      />
    </div>
  );
}
